import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild, output } from "@angular/core";
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from "ngx-popperjs";
import { IMaskDirective } from "angular-imask";
import { FormsModule } from "@angular/forms";
import { AsyncPipe, NgClass, NgFor, NgIf } from "@angular/common";
import { RibbonButtonComponent } from "../../../../shared/buttons/ribbon-button/ribbon-button.component";
import { NumericHelper, NumericMaskSettings, PopperHelper } from "@nts/std/utility";
import { BehaviorSubject } from "rxjs";
import { BigNumber } from "@nts/std/types";
import type { UICommandInterface } from '@nts/std/interfaces';

@Component({
  selector: 'nts-base-numeric-box',
  templateUrl: './base-numeric-box.component.html',
  styleUrls: ['./base-numeric-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgxPopperjsModule,
    IMaskDirective,
    FormsModule,
    NgClass,
    RibbonButtonComponent,
    NgIf,
    AsyncPipe,
    NgFor
  ]
})

export class BaseNumericBoxComponent<T = BigNumber | number> implements AfterViewInit, OnChanges, OnInit, OnDestroy {

  @Input() minValue: T;
  @Input() maxValue: T;
  @Input() maskSettings: NumericMaskSettings;
  @Input() inputMode = null;
  @Input() tabIndex = -1;
  @Input() isDisabled = false;
  @Input() value: T | '' | null = null;
  @Input() isReadonly = false;
  @Input() placeholder = "";
  @Input() customClasses = "";
  @Input() errorList: string[] = [];
  @Input() showErrorTooltip = true;
  @Input() showErrorBorder = true;
  @Input() primaryColor = null;
  @Input() customCommandList: UICommandInterface[] = [];
  @Input() defaultBorderColor = null;
  @Input() activeBorderColor = null;
  @Input() hoverBorderColor = null;
  @Input() listenClickOutside = false;

  @Output() onFinishEditing = new EventEmitter();
  @Output() onBlur = new EventEmitter();
  @Output() onFocus = new EventEmitter();
  @Output() onFocusOut = new EventEmitter();
  @Output() onKeyDown = new EventEmitter();
  @Output() valueChange = new EventEmitter();
  @Output() inputChange = new EventEmitter();
  readonly viewReady = output<void>();


  isViewReady = false;
  @ViewChild('numericBox', { static: true }) numericBox: ElementRef;
  @ViewChild(NgxPopperjsDirective, { static: true }) popperError: NgxPopperjsDirective;
  @ViewChild(IMaskDirective, { static: true }) iMask: IMaskDirective<any>;

  separatorLimit: string = '';
  nullable = true;
  currentMask = 'separator';
  currentValue: string = "";
  ngxPopperjsTriggers = NgxPopperjsTriggers;
  ngxPopperjsPlacements = NgxPopperjsPlacements;
  mask = null;
  overrideBorderColor = null;
  isActive = false;
  isHover = false;
  documentClickListener: any;
  computedInputMode$ = new BehaviorSubject<null | string>('');

  constructor(
    private cd: ChangeDetectorRef,
    private zone: NgZone,
    public el: ElementRef,
    private renderer: Renderer2,
  ) {

  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.emitViewReady());
  }

  ngOnInit(): void {
    this.updateInputMode();

    this.handleOverridesColors();

    if (this.listenClickOutside) {
      this.bindDocumentClickListener();
    }
  }

  updateInputMode() {
    this.computedInputMode$.next(this.inputMode ?? ((this.maskSettings?.decimalLimit != null && this.maskSettings?.decimalLimit > 0) ? 'decimal' : 'numeric'));
  }

  ngOnDestroy() {

    this.minValue = null;
    this.maxValue = null;
    this.inputMode = null;
    this.value = null;
    this.errorList = null;

    this.customCommandList = null;

    this.onFinishEditing?.complete();
    this.onFinishEditing = null;

    this.onBlur?.complete();
    this.onBlur = null;

    this.onFocus?.complete();
    this.onFocus = null;

    this.onFocusOut?.complete();
    this.onFocusOut = null;

    this.onKeyDown?.complete();
    this.onKeyDown = null;

    this.valueChange?.complete();
    this.valueChange = null;

    this.inputChange?.complete();
    this.inputChange = null;

    this.numericBox = null;
    this.popperError = null;
    this.iMask?.destroyMask && this.iMask.destroyMask();
    this.iMask = null;

    this.mask = null;

    this.unbindDocumentClickListener();

    this.computedInputMode$?.complete();
    this.computedInputMode$ = null;

    this.cd = null;
    this.zone = null;
    this.el = null;
    this.renderer = null;
  }

  unbindDocumentClickListener() {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = null;
    }
  }

  isOutsideClicked(event: Event) {
    return !(this.el.nativeElement.isSameNode(event.target) || this.el.nativeElement.contains(event.target));
  }

  initMaskSettings(maskSettings: NumericMaskSettings): void {
    if (maskSettings) {
      const mask = NumericHelper.getMaskConfiguration(maskSettings, this.numericBox);
      this.mask = { ...mask };
    }
  }

  applyMaskLogic(val: T | ''): T | '' {
    if (this.maskSettings) {
      if (this.maskSettings.isBigNumber) {
        // if(val?.toString()?.length > 0){
        //     val = (new BigNumber(val)).toFormat(BigNumber.getFormat(BaseNumericBoxComponent.thousandSeparator , BaseNumericBoxComponent.decimalSeparator, this.maskSettings.useThousandSeparator));
        // } else if (!this.maskSettings.nullable) {
        //   val = new BigNumber('0');
        // }
      } else {
        if (val || !this.maskSettings.nullable) {
          // if (typeof val == 'number') {
          //     val = val.toString().replace('.', BaseNumericBoxComponent.decimalSeparator);
          // }
        }
      }
    }
    return val;
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['defaultBorderColor'] || changes['activeBorderColor'] || changes['hoverBorderColor']) {
      this.handleOverridesColors();
    }

    if (changes['maskSettings'] && changes['maskSettings'].currentValue) {
      this.initMaskSettings(this.maskSettings);
      this.value = this.applyMaskLogic(this.value);
      this.updateInputMode();
    }

    if (changes['value']) {
      let value = changes['value'].currentValue == null ? '' : changes['value'].currentValue;
      this.value = this.applyMaskLogic(value);
    }

    if (changes['errorList']) {
      this.checkPopper();
    }
  }

  handleOverridesColors() {
    if (!this.defaultBorderColor || !this.activeBorderColor || !this.hoverBorderColor) {
      // devono essere impostate tutte e tre le variabili
      return;
    }

    this.overrideBorderColor = this.defaultBorderColor;
    if (this.isActive && !this.isDisabled) {
      this.overrideBorderColor = this.activeBorderColor;
    }
    if (this.isHover && !this.isDisabled) {
      this.overrideBorderColor = this.hoverBorderColor;
    }
  }

  blur(e): void {
    this.isActive = false;
    this.handleOverridesColors();
    this.popperError?.hide();
    this.onBlur.emit(e);
  }

  focus(e): void {
    this.isActive = true;
    this.handleOverridesColors();
    this.onFocus.emit(e);
  }

  onInputChange(value): void {
    this.inputChange.emit(value);
  }

  onModelChange(value: any): void {
    // NON MODIFICARE onModelChange IN INPUT, altrimenti il mask non funziona correttamente
    let stringedValue = this.numericBox.nativeElement.value === undefined ? null : this.numericBox.nativeElement.value;
    if (this.maskSettings?.nullable === true && stringedValue === '') {
      value = null;
    }
    this.valueChange.emit(value);
  }

  mouseEnter(e) {
    this.isHover = true;
    this.handleOverridesColors();
  }

  mouseLeave(e) {
    this.isHover = false;
    this.handleOverridesColors();
  }

  bindDocumentClickListener() {
    if (!this.documentClickListener) {
      this.zone.runOutsideAngular(() => {
        const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

        this.documentClickListener = this.renderer.listen(documentTarget, 'click', (event) => {
          if (this.isOutsideClicked(event)) {
            this.zone.run(() => {
              this.onFinishEditing.emit();

              this.cd?.markForCheck();
            });
          }

        });
      });
    }
  }

  private checkPopper() {
    if (this.popperError && (this.errorList == null || this.errorList.length === 0)) {
      PopperHelper.hide(this.popperError);
    }
  }

  private emitViewReady(): void {
    this.isViewReady = true;
    this.viewReady.emit();
  }
}
